/**
 * TimelineScreen
 *
 * When does the user want to start?
 */

import React, { useRef, useState } from "react";
import { View, Text, Pressable, ScrollView, Animated } from "react-native";
import { Zap, Clock, Calendar, Compass } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";

type TimelineOption = {
  id: "asap" | "3-months" | "6-months" | "exploring";
  labelKey: string;
  detailKey: string;
  icon: LucideIcon;
};

const OPTIONS: TimelineOption[] = [
  {
    id: "asap",
    labelKey: "plan_builder.timeline.asap_label",
    detailKey: "plan_builder.timeline.asap_detail",
    icon: Zap,
  },
  {
    id: "3-months",
    labelKey: "plan_builder.timeline.three_months_label",
    detailKey: "plan_builder.timeline.three_months_detail",
    icon: Clock,
  },
  {
    id: "6-months",
    labelKey: "plan_builder.timeline.six_months_label",
    detailKey: "plan_builder.timeline.six_months_detail",
    icon: Calendar,
  },
  {
    id: "exploring",
    labelKey: "plan_builder.timeline.exploring_label",
    detailKey: "plan_builder.timeline.exploring_detail",
    icon: Compass,
  },
];

function OptionButton({
  option,
  label,
  detail,
  isSelected,
  onPress,
}: {
  option: TimelineOption;
  label: string;
  detail: string;
  isSelected: boolean;
  onPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;
  const Icon = option.icon;

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
    >
      <Animated.View
        style={{
          transform: [{ scale }],
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 18,
          paddingVertical: 16,
          gap: 14,
          backgroundColor: isSelected
            ? `${EMBER.colors.text}10`
            : "rgba(255, 255, 255, 0.82)",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: isSelected
            ? EMBER.colors.text
            : "rgba(255, 255, 255, 0.9)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
        }}
      >
        <Icon
          size={20}
          color={isSelected ? EMBER.colors.text : EMBER.colors.textMid}
          strokeWidth={1.5}
        />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "500",
              color: isSelected ? EMBER.colors.text : EMBER.colors.text,
            }}
          >
            {label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {detail}
          </Text>
        </View>
      </Animated.View>
    </Pressable>
  );
}

type Props = {
  onNext: () => void;
};

export default function TimelineScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TimelineScreen");
  const { state, updateState } = usePlanBuilder();
  const [selected, setSelected] = useState(state.timeline);
  const handleSelect = (
    value: "asap" | "3-months" | "6-months" | "exploring",
  ) => {
    setSelected(value);
    updateState({ timeline: value });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.timeline.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.timeline.subtitle")}
      </Text>

      <View style={{ gap: 10 }}>
        {OPTIONS.map((option) => (
          <OptionButton
            key={option.id}
            option={option}
            label={t(option.labelKey)}
            detail={t(option.detailKey)}
            isSelected={selected === option.id}
            onPress={() => {
              capturePress(pressLabel(t(option.labelKey)), option.id);
              handleSelect(option.id);
            }}
          />
        ))}
      </View>

      {selected && (
        <View style={{ marginTop: 24 }}>
          <WarmButton
            title={t("plan_builder.continue")}
            onPress={() => onNext()}
          />
        </View>
      )}
    </ScrollView>
  );
}
